import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
	Button,
	Card,
	CardBody,
	Divider,
	FormControl,
	FormLabel,
	Grid,
	Input,
	InputGroup,
	InputRightAddon,
	Select,
	Text,
	Textarea,
	useToast,
	VStack,
} from "@chakra-ui/react";

import { updateDetailsAsync } from "../../redux/user/userSlice";
import { generalVerifier } from "../../utils/verifier";

export default function GeneralnTab({ property }) {
	const { t } = useTranslation();
	const isArchived = property.archived;
	const [isEditing, setIsEditing] = useState(false);
	const initialData = {
		keys: property.keys,
		description: property.description,
		bankAccount: property.owner.bankDetails.account,
		bankAccountCurrency: property.owner.bankDetails.currency,
		administrationFeePercent: property.owner.bankDetails.administrationFeePercent,
		county: property.location.county,
		city: property.location.city,
		area: property.location.area,
		street: property.location.street,
		buildingNumber: property.location.buildingNumber,
		blockNumber: property.location.blockNumber,
		entrance: property.location.entrance,
		apartmentNumber: property.location.apartmentNumber,
	};
	const [error, setError] = useState({});
	const toast = useToast();
	const [details, setDetails] = useState(initialData);
	const dispatch = useDispatch();
	const handleEditButtonClick = () => {
		if (isEditing) {
			console.log("Save new data");
			const payload = { details, propertyId: property.id };
			console.log(payload);
			const errors = generalVerifier({
				bankAccount: payload.details.bankAccount,
				bankAccountCurrency: payload.details.bankAccountCurrency,
				administrationFeePercent: payload.details.administrationFeePercent,
			});
			if (Object.entries(errors).length !== 0) {
				setError(errors);
				toast({
					title: "Error encountered.",
					description: "Invalid information",
					status: "error",
					duration: 2000,
					isClosable: true,
				});
				return;
			}
			setError({});
			dispatch(updateDetailsAsync(payload)).then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Update successful",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			});
		} else {
			console.log("Toggle edit button");
		}
		setIsEditing(!isEditing);
	};
	const handleDetailsChange = (e) => {
		setDetails({ ...details, [e.target.name]: e.target.value });
	};

	return (
		<>
			{/* <GridItem colSpan={{ sm: "6", lg: "3" }}> */}
			<Grid templateColumns={{ sm: "1fr", xl: "repeat(2, 1fr)" }} gap="2">
				<Card variant="outline" shadow="sm">
					<CardBody>
						<VStack spacing={6} align="stretch">
							<Text fontWeight="bold" fontSize="xl" color="blue.600">
								{t("property.location")}
							</Text>

							<VStack spacing={4} align="stretch">
								<Grid templateColumns="repeat(2, 1fr)" gap={4}>
									<FormControl>
										<FormLabel fontWeight="bold">{t("property.county")}</FormLabel>
										{isEditing ? (
											<Input
												size="md"
												isInvalid={error?.county}
												value={details.county}
												onChange={handleDetailsChange}
												name="county"
												bg="white"
												borderRadius="md"
											/>
										) : (
											<Text color="gray.700">{property.location.county || "-"}</Text>
										)}
									</FormControl>

									<FormControl>
										<FormLabel fontWeight="bold">{t("property.city")}</FormLabel>
										{isEditing ? (
											<Input size="md" isInvalid={error?.city} value={details.city} onChange={handleDetailsChange} name="city" bg="white" borderRadius="md" />
										) : (
											<Text color="gray.700">{property.location.city || "-"}</Text>
										)}
									</FormControl>

									<FormControl>
										<FormLabel fontWeight="bold">{t("property.area")}</FormLabel>
										{isEditing ? (
											<Input size="md" isInvalid={error?.area} value={details.area} onChange={handleDetailsChange} name="area" bg="white" borderRadius="md" />
										) : (
											<Text color="gray.700">{property.location.area || "-"}</Text>
										)}
									</FormControl>

									<FormControl>
										<FormLabel fontWeight="bold">{t("property.street")}</FormLabel>
										{isEditing ? (
											<Input
												size="md"
												isInvalid={error?.street}
												value={details.street}
												onChange={handleDetailsChange}
												name="street"
												bg="white"
												borderRadius="md"
											/>
										) : (
											<Text color="gray.700">{property.location.street || "-"}</Text>
										)}
									</FormControl>
									<FormControl>
										<FormLabel fontWeight="bold">{t("property.buildingNumber")}</FormLabel>
										{isEditing ? (
											<Input
												size="md"
												isInvalid={error?.buildingNumber}
												value={details.buildingNumber}
												onChange={handleDetailsChange}
												name="buildingNumber"
												bg="white"
												borderRadius="md"
											/>
										) : (
											<Text color="gray.700">{property.location.buildingNumber || "-"}</Text>
										)}
									</FormControl>

									<FormControl>
										<FormLabel fontWeight="bold">{t("property.blockNumber")}</FormLabel>
										{isEditing ? (
											<Input
												size="md"
												isInvalid={error?.blockNumber}
												value={details.blockNumber}
												onChange={handleDetailsChange}
												name="blockNumber"
												bg="white"
												borderRadius="md"
											/>
										) : (
											<Text color="gray.700">{property.location.blockNumber || "-"}</Text>
										)}
									</FormControl>
								</Grid>

								<Grid templateColumns="repeat(2, 1fr)" gap={4}>
									<FormControl>
										<FormLabel fontWeight="bold">{t("property.entrance")}</FormLabel>
										{isEditing ? (
											<Input
												size="md"
												isInvalid={error?.entrance}
												value={details.entrance}
												onChange={handleDetailsChange}
												name="entrance"
												bg="white"
												borderRadius="md"
											/>
										) : (
											<Text color="gray.700">{property.location.entrance || "-"}</Text>
										)}
									</FormControl>

									<FormControl>
										<FormLabel fontWeight="bold">{t("property.apartmentNumber")}</FormLabel>
										{isEditing ? (
											<Input
												size="md"
												isInvalid={error?.apartmentNumber}
												value={details.apartmentNumber}
												onChange={handleDetailsChange}
												name="apartmentNumber"
												bg="white"
												borderRadius="md"
											/>
										) : (
											<Text color="gray.700">{property.location.apartmentNumber || "-"}</Text>
										)}
									</FormControl>
								</Grid>

								<FormControl>
									<FormLabel fontWeight="bold">{t("property.keys")}</FormLabel>
									{isEditing ? (
										<Textarea
											size="md"
											isInvalid={error?.keys}
											value={details.keys}
											onChange={handleDetailsChange}
											name="keys"
											bg="white"
											borderRadius="md"
											rows={3}
										/>
									) : (
										<Text color="gray.700">{property.keys || "-"}</Text>
									)}
								</FormControl>
							</VStack>
						</VStack>
					</CardBody>
				</Card>

				<Card variant="outline" shadow="sm" height="100%">
					<CardBody>
						<VStack spacing={6} align="stretch">
							<FormControl>
								<FormLabel fontWeight="bold" fontSize="xl" color="blue.600">
									{t("property.description")}
								</FormLabel>
								{isEditing ? (
									<Textarea
										size="md"
										isInvalid={error?.description}
										value={details.description}
										onChange={handleDetailsChange}
										name="description"
										bg="white"
										borderRadius="md"
										rows={4}
									/>
								) : (
									<Text color="gray.700" whiteSpace={"pre-line"}>
										{property.description || "-"}
									</Text>
								)}
							</FormControl>

							<Divider />

							<VStack spacing={4} align="stretch">
								<Text fontWeight="bold" fontSize="xl" color="blue.600">
									{t("property.bankAccount")}
								</Text>

								<FormControl>
									<FormLabel fontWeight="bold">{t("property.bankAccount")}</FormLabel>
									{isEditing ? (
										<Input
											size="md"
											isInvalid={error?.bankAccount}
											value={details.bankAccount}
											onChange={handleDetailsChange}
											name="bankAccount"
											bg="white"
											borderRadius="md"
										/>
									) : (
										<Text color="gray.700">{property.owner.bankDetails.account || "-"}</Text>
									)}
								</FormControl>

								<FormControl>
									<FormLabel fontWeight="bold">{t("property.bankAccountCurrency")}</FormLabel>
									{isEditing ? (
										<Select
											isInvalid={error?.bankAccountCurrency}
											value={details.bankAccountCurrency}
											onChange={handleDetailsChange}
											name="bankAccountCurrency"
											bg="white"
											borderRadius="md"
										>
											<option value="eur">EUR</option>
											<option value="ron">RON</option>
										</Select>
									) : (
										<Text color="gray.700">{property.owner.bankDetails.currency || "-"}</Text>
									)}
								</FormControl>

								<FormControl>
									<FormLabel fontWeight="bold">{t("property.administrationFeePercentage")}</FormLabel>
									{isEditing ? (
										<InputGroup size="md">
											<Input
												isInvalid={error?.administrationFeePercent}
												name="administrationFeePercent"
												placeholder="Administration fee percent"
												value={details.administrationFeePercent}
												onChange={handleDetailsChange}
												_placeholder={{ color: "gray.500" }}
												type="number"
												bg="white"
												borderRadius="md"
											/>
											<InputRightAddon>%</InputRightAddon>
										</InputGroup>
									) : (
										<Text color="gray.700">{property.owner.bankDetails.administrationFeePercent || "-"}%</Text>
									)}
								</FormControl>
							</VStack>

							<Button width="fit-content" colorScheme="blue" onClick={handleEditButtonClick} isDisabled={isArchived} size="md" mt={4}>
								{isEditing ? t("misc.save") : t("misc.edit")}
							</Button>
						</VStack>
					</CardBody>
				</Card>
			</Grid>
			{/* </GridItem> */}
		</>
	);
}
